/**
 * SweepModal — confirm the idempotent expiry sweep (POST /qc/lots/sweep, cap qc.lot.manage). Flips any
 * past-date `valide` lot → `expire`. The success toast reports the count (none / n). Gated by useCan.
 */
import { Button, Modal, useToast } from "@/ui";
import { useCan } from "@/auth";
import { useT } from "@/app";
import { ApiError } from "@/api";
import { QC_CAPS } from "../caps";
import { useLabId, useSweepLots } from "../api";

export interface SweepModalProps {
  open: boolean;
  onClose: () => void;
  onSwept: () => void;
}

export function SweepModal({ open, onClose, onSwept }: SweepModalProps) {
  const { t } = useT();
  const toast = useToast();
  const labId = useLabId();
  const canManage = useCan(QC_CAPS.lotManage);
  const mutation = useSweepLots(labId);

  const handleClose = () => {
    if (mutation.isPending) return;
    onClose();
  };

  const run = () => {
    if (!canManage) return;
    mutation.mutate(
      {},
      {
        onSuccess: (res) => {
          const n = res.swept.length;
          if (n === 0) toast.info(t("qc.lots.sweep.success.none"));
          else toast.success(t("qc.lots.sweep.success.some", { n }));
          onSwept();
        },
        onError: (err) => toast.error(err instanceof ApiError ? err.message : t("qc.field.required")),
      },
    );
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={t("qc.lots.sweep.title")}
      footer={
        <>
          <Button variant="ghost" onClick={handleClose} disabled={mutation.isPending}>
            {t("qc.action.cancel")}
          </Button>
          <Button variant="danger" onClick={run} loading={mutation.isPending} disabled={!canManage}>
            {t("qc.lots.sweep.submit")}
          </Button>
        </>
      }
    >
      <p style={{ margin: 0, color: "var(--nv-slate)" }}>{t("qc.lots.sweep.warn")}</p>
    </Modal>
  );
}
